import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { animated, useSpring } from '@react-spring/web';
import ProgressIndicator from 'components/common/progress-indicator';
import Button from 'components/inputs/button';
import ToggleSwitch from 'components/inputs/toggle-switch';
import InfoTooltip from 'components/layout/info-tooltip';
import { ReactNode, useEffect, useRef, useState } from 'react';

type AccessoryDrawerProps = {
  title: string;
  subtitle?: string;
  description: string;
  required: boolean;
  itemSelected: boolean;
  notNeeded: boolean;
  children?: ReactNode;
  handleNotNeeded: (value: boolean) => void;
  disabled?: boolean;
  warning?: string;
  hideSwitch?: boolean;
  switchDisabled?: boolean;
  alwaysOpen?: boolean;
  defaultOpen?: boolean;
};

export default function AccessoryDrawer({
  title,
  subtitle,
  description,
  required,
  itemSelected,
  notNeeded,
  children,
  handleNotNeeded,
  disabled,
  warning,
  hideSwitch,
  switchDisabled,
  alwaysOpen,
  defaultOpen = true
}: AccessoryDrawerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [contentHeight, setContentHeight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const heightMeasuredRef = useRef(false);

  useEffect(() => {
    if (!drawerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setContentHeight(entry.contentRect.height);
      });
    });

    observer.observe(drawerRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  const [spring, springApi] = useSpring(() => ({
    height: 0,
    config: { tension: 300, friction: 30 }
  }));

  useEffect(() => {
    const immediate = !heightMeasuredRef.current;
    if (contentHeight > 0) {
      heightMeasuredRef.current = true;
    }
    springApi.start({ height: open ? contentHeight : 0, immediate });
  }, [open, contentHeight, springApi]);

  useEffect(() => {
    if (disabled && !alwaysOpen) {
      setOpen(false);
    }
  }, [disabled, alwaysOpen]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
  };

  return (
    <div
      ref={rootRef}
      className={`flex w-full flex-col rounded-lg bg-white ${
        disabled && 'opacity-50'
      }`}
    >
      <div className='flex items-center gap-2 p-5'>
        {required && <ProgressIndicator checked={itemSelected} />}
        <div className='flex flex-col'>
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold text-gray-800'>{title}</span>
            <InfoTooltip>{description}</InfoTooltip>
            {warning && <span className='ml-2 text-amber-500'>{warning}</span>}
          </div>
          {subtitle && (
            <span className='text-sm text-gray-500'>{subtitle}</span>
          )}
        </div>
        <div className='ml-auto flex items-center gap-3'>
          {!hideSwitch && (
            <div
              className={`flex items-center gap-2 p-2 ${
                disabled || switchDisabled
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <ToggleSwitch
                value={!notNeeded}
                onChange={(checked) => handleNotNeeded(!checked)}
                disabled={disabled || switchDisabled}
              />
              <button
                type='button'
                className='whitespace-nowrap text-sm text-gray-600 disabled:cursor-not-allowed'
                disabled={disabled || switchDisabled}
                onClick={() => handleNotNeeded(!notNeeded)}
              >
                Include Recommended
              </button>
            </div>
          )}
          {!alwaysOpen && (
            <Button
              size='lg'
              color='secondary'
              text={open ? 'Close' : 'Select'}
              icon={open ? ChevronUpIcon : ChevronDownIcon}
              iconSize='lg'
              iconPosition='right'
              onClick={toggleOpen}
              disabled={disabled}
            />
          )}
        </div>
      </div>
      <animated.div style={spring} className='overflow-hidden'>
        <div ref={drawerRef} className='w-full border-t border-gray-200'>
          {children}
        </div>
      </animated.div>
    </div>
  );
}
